import { FunctionComponent, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { format } from "date-fns";

import { TicketDetail } from "@signalist/shared-types";

import { ROUTE_CONFIG } from "@/routes/config";

import { AccountTabs } from "@/pages/Private/pages/Account/accountTabs";

import { TicketStatusPill } from "../../../components/TicketPills";
import { ticketLinkedEntityKey } from "../../../utils/ticketRowMeta";
import {
	allTicketAttachments,
	ticketAutoCloseDate,
	ticketResolvedBy,
	ticketUserFirstName,
	ticketUserName,
} from "../utils/ticketThread";
import { TicketAttachmentChip } from "./TicketAttachmentChip";
import { TICKET_DATE_FORMAT } from "./TicketMessage";

interface TicketDetailRailProps {
	ticket: TicketDetail;
	onAttachmentError: (fileName: string) => void;
}

const Heading: FunctionComponent<{ children: ReactNode }> = ({ children }) => (
	<h4 className="mt-4 text-[11px] font-semibold uppercase tracking-wide text-text-meta first:mt-0">
		{children}
	</h4>
);

const Row: FunctionComponent<{ label: string; children: ReactNode }> = ({ label, children }) => (
	<>
		<span className="text-text-meta">{label}</span>
		<span className="text-text">{children}</span>
	</>
);

/** The mockup's `.rail`: status + explanation, details, attachments, notifications hint. */
export const TicketDetailRail: FunctionComponent<TicketDetailRailProps> = ({
	ticket,
	onAttachmentError,
}) => {
	const { t } = useTranslation();
	const linked = ticketLinkedEntityKey(ticket);
	const attachments = allTicketAttachments(ticket);
	const closeDate = ticketAutoCloseDate(ticket);
	const statusDate = ticket.closedAt ?? ticket.resolvedAt ?? ticket.updatedAt;

	const explanation = t(`support.detail.rail.explanation.${ticket.status}`, {
		name: ticketUserFirstName(ticketResolvedBy(ticket)) || "Signalist",
		date: format(new Date(statusDate), TICKET_DATE_FORMAT),
		closeDate: closeDate ? format(closeDate, "MMM d") : "",
	});

	return (
		<aside className="flex w-[280px] shrink-0 flex-col text-xs">
			<Heading>{t("support.detail.rail.status")}</Heading>
			<div className="mt-1.5 flex flex-col items-start gap-2 rounded-xl border border-gray-200 p-3">
				<TicketStatusPill status={ticket.status} />
				<span className="text-text-meta">{explanation}</span>
			</div>

			<Heading>{t("support.detail.rail.details")}</Heading>
			<div className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
				<Row label={t("support.detail.rail.ticket")}>
					{t("support.detail.ticketNumber", { id: ticket.id })}
				</Row>
				<Row label={t("support.detail.rail.category")}>
					{t(`support.category.${ticket.category}`)}
				</Row>
				<Row label={t("support.detail.rail.opened")}>
					{format(new Date(ticket.createdAt), TICKET_DATE_FORMAT)}
				</Row>
				<Row label={t("support.detail.rail.openedBy")}>{ticketUserName(ticket.createdBy)}</Row>
				{linked && (
					<Row label={t("support.detail.rail.relatedTo")}>
						{t(`support.linkedEntity.${linked.key}`, { id: linked.id })}
					</Row>
				)}
				<Row label={t("support.detail.rail.source")}>
					{t(`support.detail.source.${ticket.source}`)}
				</Row>
			</div>

			<Heading>{t("support.detail.rail.attachments")}</Heading>
			<div className="mt-1.5 flex flex-col items-start gap-1.5">
				{attachments.length === 0 ? (
					<span className="text-text-meta">{t("support.detail.attachments.none")}</span>
				) : (
					attachments.map((attachment) => (
						<TicketAttachmentChip
							key={attachment.id}
							attachment={attachment}
							onError={onAttachmentError}
						/>
					))
				)}
			</div>

			<Heading>{t("support.detail.rail.notifications")}</Heading>
			<p className="mt-1.5 leading-relaxed text-text-meta">
				{t("support.detail.rail.notificationsHint")}{" "}
				<Link
					className="font-medium text-brand-light"
					to={`${ROUTE_CONFIG.ACCOUNT}?tab=${AccountTabs.NOTIFICATIONS}`}
				>
					{t("support.detail.rail.change")}
				</Link>
			</p>
		</aside>
	);
};
